import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import './ClubCreate.css';

/* ─────────────────────────────────────────────────────────────
   CLUB CREATION PAGE — /clubs/new
   Simple single-card form to create a new club.
   Calls the create_club SECURITY DEFINER RPC which generates
   a join code and adds the creator as an admin member.
   ───────────────────────────────────────────────────────────── */
function ClubCreate() {
  const { user }    = useAuth();
  const navigate    = useNavigate();

  // ── Form state ─────────────────────────────────────────────
  const [name,        setName]        = useState('');
  const [description, setDescription] = useState('');
  const [isPublic,    setIsPublic]    = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState('');

  // ── Submit ─────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    if (!user || submitting) return;

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Please enter a club name.');
      return;
    }

    setSubmitting(true);
    setError('');

    const { data, error: rpcError } = await supabase.rpc('create_club', {
      p_name:        trimmedName,
      p_description: description.trim() || null,
      p_is_public:   isPublic,
    });

    if (rpcError) {
      // Surface friendly messages for known errors
      if (rpcError.message?.includes('unique') || rpcError.message?.includes('duplicate')) {
        setError('That club name is already taken. Please choose a different name.');
      } else {
        setError(rpcError.message || 'Something went wrong. Please try again.');
      }
      setSubmitting(false);
      return;
    }

    // Navigate to the new club's page
    navigate('/clubs/' + data.club_id);
  }

  return (
    <div className="cc-page">
      <div className="cc-container">

        {/* Back link + header */}
        <div className="cc-header">
          <Link to="/clubs" className="cc-back">← Clubs</Link>
          <h1 className="cc-title">Create a Club</h1>
          <p className="cc-subtitle">
            Clubs give you a private leaderboard and match history with your group —
            perfect for offices, gyms, and friend circles.
          </p>
        </div>

        {/* Form card */}
        <form className="cc-card" onSubmit={handleSubmit}>

          {/* Club name */}
          <div className="cc-field">
            <label className="cc-label" htmlFor="cc-name">Club Name</label>
            <input
              id="cc-name"
              className="cc-input"
              type="text"
              placeholder="e.g. Office Table Tennis Club"
              maxLength={60}
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </div>

          {/* Description */}
          <div className="cc-field">
            <label className="cc-label" htmlFor="cc-desc">
              Description <span className="cc-optional">(optional)</span>
            </label>
            <textarea
              id="cc-desc"
              className="cc-textarea"
              placeholder="A short description of your club..."
              maxLength={280}
              rows={3}
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>

          {/* Public / Private toggle */}
          <div className="cc-field">
            <div className="cc-toggle-row">
              <div className="cc-toggle-labels">
                <span className="cc-label">Visibility</span>
                <span className="cc-toggle-value">
                  {isPublic ? 'Public' : 'Private'}
                </span>
              </div>
              <button
                type="button"
                className={`lb-toggle${isPublic ? ' lb-toggle-on' : ''}`}
                onClick={() => setIsPublic(v => !v)}
                aria-label="Toggle club visibility"
              >
                <span className="lb-toggle-knob" />
              </button>
            </div>
            <p className="cc-toggle-hint">
              {isPublic
                ? 'Anyone can find and join this club by searching in the Clubs hub.'
                : 'Only people with your join code or invite link can find and join this club.'}
            </p>
          </div>

          {/* Error */}
          {error && <p className="cc-error">{error}</p>}

          {/* Submit */}
          <button
            type="submit"
            className="btn-primary cc-submit"
            disabled={submitting || !name.trim()}
          >
            {submitting ? 'Creating…' : 'Create Club'}
          </button>

        </form>
      </div>
    </div>
  );
}

export default ClubCreate;
